"use client";

import { useMemo, useState, useCallback, useRef, Ref, useEffect } from "react";
import { Check, LoaderCircle, Plus, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Avatar, AvatarFallback } from "../../ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "@/lib/utils";
import { useApps } from "@/hooks/use-apps";
import { usePyloUsers } from "@/hooks/use-pylo-users";
import { useDebounce } from "@/lib/client-utils";
import { QueryInput, EntityInstance } from "@/services/entity-instance.service";
import { PyloUserListItem } from "@/services/pylo-user.service";
import { useEntityInstanceModalStack } from "@/stores/entity-instance-modal-stack-store";
import { useInView } from "@/hooks/ui-helpers/use-in-view";
import { OptionType, SelectedOptionsType } from "./relation-instance-select";
import { useRelationEntityList } from "@/hooks/use-relation-entity-list";

interface PyloUserRelationSelectProps {
  isSingle: boolean;
  selectedInstance: EntityInstance | null;
  selectedInstances: EntityInstance[];
  onSelect?: (instance: EntityInstance) => void;
  onUnselect?: (id: string) => void;
  idsToDisconnect: Set<string>;
  parentEntityId?: string;
  parentEntityName: string;
  fieldName: string;
}

// Reusable user display component
interface PyloUserDisplayProps {
  user: PyloUserListItem;
  size?: "sm" | "md";
  showApp?: boolean;
  appLayout?: "badge" | "inline";
}

const PyloUserDisplay: React.FC<PyloUserDisplayProps> = ({
  user,
  size = "md",
  showApp = true,
  appLayout = "badge",
}) => {
  const appName = user.pylo_app?.data?.name;
  const initial = user.email.charAt(0).toUpperCase();
  const showInline = showApp && appName && appLayout === "inline";
  const showBadge = showApp && appName && appLayout === "badge";

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar
        className={cn("flex-shrink-0", size === "sm" ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs")}
      >
        <AvatarFallback className="bg-primary/10 font-medium text-primary">
          {initial}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className={cn("truncate", size === "sm" ? "text-xs" : "text-sm")}>
          {user.email}
          {showInline && <span className="text-muted-foreground"> · {appName}</span>}
        </span>
        {showBadge && (
          <Badge
            variant="outline"
            className="h-5 w-fit px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground"
          >
            {appName}
          </Badge>
        )}
      </div>
    </div>
  );
};

export const PyloUserRelationSelect: React.FC<PyloUserRelationSelectProps> = ({
  isSingle,
  selectedInstance,
  selectedInstances,
  onSelect,
  onUnselect,
  idsToDisconnect,
  parentEntityId,
  parentEntityName,
  fieldName,
}) => {
  const { push } = useEntityInstanceModalStack();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 300);

  // Apps state - single selection, null means "All" (no filter)
  const { data: appsData, isLoading: appsLoading } = useApps();
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize with default app when apps load (only once)
  useEffect(() => {
    if (appsData && !hasInitialized) {
      const defaultApp = appsData.find((app) => app.is_default);
      if (defaultApp) {
        setSelectedAppId(defaultApp.id);
      }
      setHasInitialized(true);
    }
  }, [appsData, hasInitialized]);

  // Build query for filtering users
  const userQuery = useMemo<QueryInput[]>(() => {
    const conditions: QueryInput[] = [];

    // Search by email
    if (debouncedSearch) {
      conditions.push({
        condition: {
          field: "email",
          operator: "ilike",
          value: `%${debouncedSearch}%`,
        },
      });
    }

    // Filter by selected app (null means "All" - no filter)
    if (selectedAppId) {
      conditions.push({
        condition: {
          field: "pylo_app.id",
          operator: "equal" as const,
          value: selectedAppId,
        },
      });
    }

    return conditions;
  }, [debouncedSearch, selectedAppId]);

  // Fetch users with filters
  const usersQuery = usePyloUsers(userQuery, 20);

  // Fetch selected users for display
  const selectedUsersQuery = useRelationEntityList({
    fieldName: fieldName,
    id: parentEntityId,
    entityName: parentEntityName,
    fieldsToFetchForRelationFieldNames: [
      {
        fieldName: fieldName,
        fieldsToFetch: ["id", "email"],
      },
    ],
  });

  // Store newly created/selected users for display
  const [bufferedUsers, setBufferedUsers] = useState<PyloUserListItem[]>([]);

  // Stable cache of all users ever seen from the dropdown — survives search changes
  const seenUsersRef = useRef(new Map<string, PyloUserListItem>());

  // Accumulate into the stable cache: server-connected users first (fallback), then
  // dropdown results (richer data) on top so they take precedence
  const serverItems =
    (selectedUsersQuery as SelectedOptionsType)?.data?.pages
      ?.flatMap((page) => (Array.isArray(page.data) ? page.data : [page.data]))
      .filter((x) => x !== null) ?? [];
  serverItems.forEach((item) => {
    const typed = item as { id: string; email?: string };
    if (typed.id && !seenUsersRef.current.has(typed.id)) {
      seenUsersRef.current.set(typed.id, typed as unknown as PyloUserListItem);
    }
  });
  const dropdownUsers = usersQuery.data?.pages.flatMap((p) => p.data) ?? [];
  dropdownUsers.forEach((u) => seenUsersRef.current.set(u.id, u));
  bufferedUsers.forEach((u) => seenUsersRef.current.set(u.id, u));

  const selectedUsersMap = seenUsersRef.current;

  // Calculate totals
  const pages = (selectedUsersQuery as SelectedOptionsType)?.data?.pages.filter((x) => !!x) ?? [];

  const selectedOptionElements = pages
    .flatMap((page) =>
      Array.isArray(page.data) ? page.data.map((o) => o as OptionType) : [page.data as OptionType],
    )
    .filter((x): x is OptionType => !!x);

  const serverSelectedIds = new Set(selectedOptionElements.map((x) => x.id));
  const selectedIds = new Set(
    (Array.isArray(selectedInstances) ? selectedInstances : []).map((y) => y.id),
  );
  const newlyAddedIds = new Set(Array.from(selectedIds).filter((id) => !serverSelectedIds.has(id)));
  const serverDisconnectedCount = Array.from(idsToDisconnect).filter((id) =>
    serverSelectedIds.has(id),
  ).length;

  const serverTotal = pages[pages.length - 1]?.pagination?.total ?? 0;
  const totalToDisplay = serverTotal - serverDisconnectedCount + newlyAddedIds.size;

  // Push create modal
  const pushCreateModal = useCallback(
    (id?: string) => {
      push({
        key: `PyloUser-${id}-${crypto.randomUUID()}`,
        entityName: "PyloUser",
        entityInstanceId: id,
        onSave: ({ data }) => {
          const userId = (data as { id?: string }).id;
          if (userId) {
            onSelect?.({ id: userId });
            setBufferedUsers((prev) => {
              const existingIndex = prev.findIndex((x) => x.id === userId);
              if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  ...(data as unknown as PyloUserListItem),
                };
                return updated;
              }
              return [...prev, data as unknown as PyloUserListItem];
            });
          }
          return Promise.resolve();
        },
      });
    },
    [push, onSelect],
  );

  const isSelected = (id: string) => {
    return selectedInstances.some((x) => x.id === id);
  };

  const handleInView = () => {
    if (!usersQuery.isLoading && !usersQuery.isFetching && usersQuery.hasNextPage) {
      usersQuery.fetchNextPage();
    }
  };

  const selectApp = (appId: string | null) => {
    setSelectedAppId(appId);
  };

  // Get selected user for single selection display
  const selectedSingleUser = useMemo(() => {
    if (!selectedInstance) {
      return null;
    }
    return selectedUsersMap.get(selectedInstance.id) || null;
  }, [selectedInstance, selectedUsersMap]);

  return (
    <div className="relative">
      {!isSingle && (
        <div className="absolute -top-5 right-1 text-xs text-neutral-500">
          {selectedUsersQuery.isFetching ? (
            <span>
              <LoaderCircle className="animate-spin" size={12} />
            </span>
          ) : (
            <span>({totalToDisplay})</span>
          )}
        </div>
      )}

      {/* Single selection display - show selected user with edit/remove */}
      {isSingle && selectedSingleUser && (
        <div className="flex h-10 items-center justify-between rounded-md border px-3">
          <button
            type="button"
            className="flex min-w-0 flex-1 text-left hover:opacity-80"
            onClick={() => pushCreateModal(selectedInstance?.id)}
          >
            <PyloUserDisplay user={selectedSingleUser} size="sm" appLayout="inline" />
          </button>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            className="h-6 w-6 flex-shrink-0 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => onUnselect?.(selectedInstance?.id || "")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Select button - only show when no user selected (single) or always (multi) */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            type="button"
            className={cn(
              "w-full justify-start text-muted-foreground",
              isSingle && selectedSingleUser && "invisible absolute",
            )}
          >
            <Plus className="mr-2 h-4 w-4" />
            Select User...
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[400px] p-0" noPortal align="start" side="right">
          <Command shouldFilter={false}>
            {/* App filter chips - single select */}
            <div className="flex flex-wrap gap-1.5 border-b p-2">
              {appsLoading ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Badge
                    variant={selectedAppId === null ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => selectApp(null)}
                  >
                    All
                  </Badge>
                  {appsData?.map((app) => (
                    <Badge
                      key={app.id}
                      variant={selectedAppId === app.id ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => selectApp(app.id)}
                    >
                      {app.name}
                    </Badge>
                  ))}
                </>
              )}
            </div>

            <CommandInput
              placeholder="Search by email..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                {usersQuery.isFetching ? "Loading..." : "No users found."}
              </CommandEmpty>
              <CommandGroup>
                {usersQuery.data?.pages.flatMap((page) =>
                  page.data.map((user) => {
                    const selected = isSelected(user.id);

                    return (
                      <CommandItem
                        className={cn(
                          "cursor-pointer data-[selected=true]:bg-transparent data-[selected=true]:hover:bg-accent",
                        )}
                        key={user.id}
                        value={user.id}
                        onSelect={() => {
                          if (selected) {
                            onUnselect?.(user.id);
                          } else {
                            setBufferedUsers((prev) => [...prev, user]);
                            onSelect?.({ id: user.id });
                          }
                          if (isSingle) {
                            setOpen(false);
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            "h-3 w-3 flex-shrink-0",
                            selected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <PyloUserDisplay user={user} size="sm" />
                      </CommandItem>
                    );
                  }),
                )}
              </CommandGroup>
              {open && <InViewTrigger inView={handleInView} />}
              {usersQuery.isFetchingNextPage && (
                <p className="text-center text-xs">Fetching more...</p>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected items list for multi-select */}
      {!isSingle && totalToDisplay > 0 && (
        <PyloUserSelectedItemList
          idsToDisconnect={idsToDisconnect}
          selectedUsersQuery={selectedUsersQuery as SelectedOptionsType}
          selectedInstances={selectedInstances}
          usersMap={selectedUsersMap}
          onDelete={onUnselect}
          onOptionClick={pushCreateModal}
        />
      )}
    </div>
  );
};

// Selected items list component for PyloUser
interface PyloUserSelectedItemListProps {
  idsToDisconnect: Set<string>;
  selectedUsersQuery: SelectedOptionsType;
  selectedInstances: EntityInstance[];
  usersMap: Map<string, PyloUserListItem>;
  onDelete?: (id: string) => void;
  onOptionClick?: (id: string) => void;
}

const PyloUserSelectedItemList: React.FC<PyloUserSelectedItemListProps> = ({
  idsToDisconnect,
  selectedUsersQuery,
  selectedInstances,
  usersMap,
  onDelete,
  onOptionClick,
}) => {
  const serverItems =
    selectedUsersQuery.data?.pages
      .flatMap((page) => (Array.isArray(page.data) ? page.data : [page.data]))
      .filter((x) => x !== null) ?? [];

  const serverIds = new Set(serverItems.map((x) => (x as { id: string }).id));
  const newlyAddedItems = selectedInstances.filter(
    (x) => !serverIds.has(x.id) && !idsToDisconnect.has(x.id),
  );

  return (
    <div className="mt-2 flex flex-col gap-1">
      {/* Server items */}
      {serverItems.map((item) => {
        const typedItem = item as { id: string };
        if (idsToDisconnect.has(typedItem.id)) return null;
        return (
          <SelectedUserItem
            key={typedItem.id}
            id={typedItem.id}
            user={usersMap.get(typedItem.id)}
            onDelete={onDelete}
            onOptionClick={onOptionClick}
          />
        );
      })}
      {/* Newly added items */}
      {newlyAddedItems.map((item) => (
        <SelectedUserItem
          key={item.id}
          id={item.id}
          user={usersMap.get(item.id)}
          onDelete={onDelete}
          onOptionClick={onOptionClick}
          isNew
        />
      ))}
    </div>
  );
};

interface SelectedUserItemProps {
  id: string;
  user?: PyloUserListItem;
  onDelete?: (id: string) => void;
  onOptionClick?: (id: string) => void;
  isNew?: boolean;
}

const SelectedUserItem: React.FC<SelectedUserItemProps> = ({
  id,
  user,
  onDelete,
  onOptionClick,
  isNew,
}) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-md border px-3 py-2",
        isNew && "border-dashed border-primary/50 bg-primary/5",
      )}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 text-left hover:opacity-80"
        onClick={() => onOptionClick?.(id)}
      >
        {user ? (
          <PyloUserDisplay user={user} size="sm" />
        ) : (
          <span className="text-sm text-muted-foreground">{id}</span>
        )}
      </button>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        className="h-6 w-6 flex-shrink-0 p-0 text-muted-foreground hover:text-destructive"
        onClick={() => onDelete?.(id)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// InView trigger for infinite scroll
interface InViewTriggerProps {
  inView: () => void;
}

const InViewTrigger: React.FC<InViewTriggerProps> = ({ inView }) => {
  const [ref, isInView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (isInView) {
      inView();
    }
  }, [isInView, inView]);

  return <div ref={ref as Ref<HTMLDivElement>}></div>;
};
